import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Snackbar,
  Divider,
  Paper,
  Tabs,
  Tab,
  InputAdornment,
  IconButton,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SaveIcon from '@mui/icons-material/Save';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import apiClient, { getApiErrorInfo } from '../../services/api';

interface GeneralSettings {
  platformName: string;
  supportEmail: string;
  maintenanceMode: boolean;
  maintenanceMessage: string;
}

interface EmailSettings {
  sendGridApiKey: string;
  fromName: string;
  fromEmail: string;
  replyToEmail: string;
}

interface DoceboSettings {
  baseUrl: string;
  clientId: string;
  clientSecret: string;
  catalogId: string;
}

interface CpeSettings {
  defaultCreditHoursPerCourse: number;
  complianceYearStart: string;
  minimumAnnualHours: number;
  gracePeriodDays: number;
}

interface BillingSettings {
  chargebeeSiteName: string;
  chargebeeApiKey: string;
  chargebeePublishableKey: string;
}

interface SearchSettings {
  meilisearchHost: string;
  meilisearchApiKey: string;
}

interface SystemSettingsData {
  general: GeneralSettings;
  email: EmailSettings;
  docebo: DoceboSettings;
  cpe: CpeSettings;
  billing: BillingSettings;
  search: SearchSettings;
}

const DEFAULT_SETTINGS: SystemSettingsData = {
  general: {
    platformName: 'CPAx Academy',
    supportEmail: 'hello@cpacrossings.com',
    maintenanceMode: false,
    maintenanceMessage: 'The platform is currently undergoing maintenance. Please check back later.',
  },
  email: {
    sendGridApiKey: '',
    fromName: 'CPAx Academy',
    fromEmail: 'noreply@cpaxacademy.com',
    replyToEmail: 'hello@cpacrossings.com',
  },
  docebo: {
    baseUrl: '',
    clientId: '',
    clientSecret: '',
    catalogId: '',
  },
  cpe: {
    defaultCreditHoursPerCourse: 1,
    complianceYearStart: '01-01',
    minimumAnnualHours: 40,
    gracePeriodDays: 30,
  },
  billing: {
    chargebeeSiteName: '',
    chargebeeApiKey: '',
    chargebeePublishableKey: '',
  },
  search: {
    meilisearchHost: 'http://localhost:7700',
    meilisearchApiKey: '',
  },
};

interface TabPanelProps {
  children: React.ReactNode;
  value: number;
  index: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ pt: 3 }}>
      {value === index && children}
    </Box>
  );
}

function SectionHeader({ title, description }: { title: string; description?: string }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{description}</Typography>
      )}
      <Divider sx={{ mt: 1.5 }} />
    </Box>
  );
}

export default function SystemSettings() {
  const [tab, setTab] = useState(0);
  const [settings, setSettings] = useState<SystemSettingsData>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  const [showSendGridKey, setShowSendGridKey] = useState(false);
  const [showDoceboSecret, setShowDoceboSecret] = useState(false);
  const [showChargebeeKey, setShowChargebeeKey] = useState(false);
  const [showMeilisearchKey, setShowMeilisearchKey] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  async function loadSettings() {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await apiClient.get('/api/admin/settings');
      setSettings({ ...DEFAULT_SETTINGS, ...res.data });
    } catch (error: unknown) {
      // 404 means the endpoint doesn't exist yet — use defaults silently
      const info = getApiErrorInfo(error);
      if (info.status !== 404) {
        setLoadError('Could not load settings. Showing defaults.');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      await apiClient.put('/api/admin/settings', settings);
      setSnackbar({ open: true, message: 'Settings saved successfully.', severity: 'success' });
    } catch (err) {
      const info = getApiErrorInfo(err);
      setSnackbar({ open: true, message: info.message || 'Failed to save settings.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  }

  function setGeneral(patch: Partial<GeneralSettings>) {
    setSettings((s) => ({ ...s, general: { ...s.general, ...patch } }));
  }
  function setEmail(patch: Partial<EmailSettings>) {
    setSettings((s) => ({ ...s, email: { ...s.email, ...patch } }));
  }
  function setDocebo(patch: Partial<DoceboSettings>) {
    setSettings((s) => ({ ...s, docebo: { ...s.docebo, ...patch } }));
  }
  function setCpe(patch: Partial<CpeSettings>) {
    setSettings((s) => ({ ...s, cpe: { ...s.cpe, ...patch } }));
  }
  function setBilling(patch: Partial<BillingSettings>) {
    setSettings((s) => ({ ...s, billing: { ...s.billing, ...patch } }));
  }
  function setSearch(patch: Partial<SearchSettings>) {
    setSettings((s) => ({ ...s, search: { ...s.search, ...patch } }));
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Action toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Configure platform-wide settings and integrations.
        </Typography>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
        >
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </Box>

      {loadError && <Alert severity="warning" sx={{ mb: 3 }} onClose={() => setLoadError(null)}>{loadError}</Alert>}

      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab label="General" />
          <Tab label="Email" />
          <Tab label="Billing" />
          <Tab label="Docebo Integration" />
          <Tab label="CPE Compliance" />
          <Tab label="Search" />
        </Tabs>

        <Box sx={{ px: 3, pb: 3 }}>
          {/* General */}
          <TabPanel value={tab} index={0}>
            <SectionHeader title="Platform" description="Basic platform identity and behavior." />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: 560 }}>
              <TextField
                label="Platform Name"
                value={settings.general.platformName}
                onChange={(e) => setGeneral({ platformName: e.target.value })}
                fullWidth
              />
              <TextField
                label="Support Email"
                type="email"
                value={settings.general.supportEmail}
                onChange={(e) => setGeneral({ supportEmail: e.target.value })}
                fullWidth
              />
            </Box>

            <Box sx={{ mt: 4 }}>
              <SectionHeader title="Maintenance Mode" description="Take the platform offline for users while preserving admin access." />
            </Box>
            <Alert
              severity="info"
              icon={<InfoOutlinedIcon fontSize="inherit" />}
              sx={{ mb: 2.5, maxWidth: 560 }}
            >
              Maintenance mode is <strong>not yet implemented</strong> in the backend. Saving this toggle has no effect until the enforcement middleware is added.
            </Alert>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: 560 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.general.maintenanceMode}
                    onChange={(e) => setGeneral({ maintenanceMode: e.target.checked })}
                    color="warning"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>Enable Maintenance Mode</Typography>
                    <Typography variant="caption" color="text.secondary">Non-admin users will see the maintenance message.</Typography>
                  </Box>
                }
              />
              <TextField
                label="Maintenance Message"
                multiline
                minRows={2}
                value={settings.general.maintenanceMessage}
                onChange={(e) => setGeneral({ maintenanceMessage: e.target.value })}
                fullWidth
                disabled={!settings.general.maintenanceMode}
              />
            </Box>
          </TabPanel>

          {/* Email */}
          <TabPanel value={tab} index={1}>
            <SectionHeader title="SendGrid Configuration" description="Transactional email settings via SendGrid." />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: 560 }}>
              <TextField
                label="SendGrid API Key"
                type={showSendGridKey ? 'text' : 'password'}
                value={settings.email.sendGridApiKey}
                onChange={(e) => setEmail({ sendGridApiKey: e.target.value })}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowSendGridKey((v) => !v)}>
                        {showSendGridKey ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="From Name"
                value={settings.email.fromName}
                onChange={(e) => setEmail({ fromName: e.target.value })}
                fullWidth
              />
              <TextField
                label="From Email"
                type="email"
                value={settings.email.fromEmail}
                onChange={(e) => setEmail({ fromEmail: e.target.value })}
                fullWidth
              />
              <TextField
                label="Reply-To Email"
                type="email"
                value={settings.email.replyToEmail}
                onChange={(e) => setEmail({ replyToEmail: e.target.value })}
                fullWidth
              />
            </Box>
          </TabPanel>

          {/* Billing */}
          <TabPanel value={tab} index={2}>
            <SectionHeader title="Chargebee" description="Billing and subscription management via Chargebee." />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: 560 }}>
              <TextField
                label="Site Name"
                placeholder="your-site"
                value={settings.billing.chargebeeSiteName}
                onChange={(e) => setBilling({ chargebeeSiteName: e.target.value })}
                fullWidth
                helperText="The Chargebee site subdomain (e.g., picpa-test for picpa-test.chargebee.com)."
              />
              <TextField
                label="API Key (Secret)"
                type={showChargebeeKey ? 'text' : 'password'}
                value={settings.billing.chargebeeApiKey}
                onChange={(e) => setBilling({ chargebeeApiKey: e.target.value })}
                fullWidth
                helperText="Used for server-side Chargebee API calls."
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowChargebeeKey((v) => !v)}>
                        {showChargebeeKey ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Publishable API Key"
                value={settings.billing.chargebeePublishableKey}
                onChange={(e) => setBilling({ chargebeePublishableKey: e.target.value })}
                fullWidth
                helperText="Safe to expose to the frontend for hosted checkout pages."
              />
            </Box>
          </TabPanel>

          {/* Docebo */}
          <TabPanel value={tab} index={3}>
            <SectionHeader title="Docebo LMS" description="Connection settings for the Docebo course catalog integration." />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: 560 }}>
              <TextField
                label="Docebo Base URL"
                placeholder="https://yoursite.docebosaas.com"
                value={settings.docebo.baseUrl}
                onChange={(e) => setDocebo({ baseUrl: e.target.value })}
                fullWidth
              />
              <TextField
                label="Client ID"
                value={settings.docebo.clientId}
                onChange={(e) => setDocebo({ clientId: e.target.value })}
                fullWidth
              />
              <TextField
                label="Client Secret"
                type={showDoceboSecret ? 'text' : 'password'}
                value={settings.docebo.clientSecret}
                onChange={(e) => setDocebo({ clientSecret: e.target.value })}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowDoceboSecret((v) => !v)}>
                        {showDoceboSecret ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Catalog ID"
                value={settings.docebo.catalogId}
                onChange={(e) => setDocebo({ catalogId: e.target.value })}
                fullWidth
                helperText="The Docebo catalog ID to pull courses from during import."
              />
            </Box>
          </TabPanel>

          {/* CPE Compliance */}
          <TabPanel value={tab} index={4}>
            <SectionHeader title="CPE Requirements" description="Default compliance thresholds used across the platform." />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: 560 }}>
              <TextField
                label="Default Credit Hours Per Course"
                type="number"
                value={settings.cpe.defaultCreditHoursPerCourse}
                onChange={(e) => setCpe({ defaultCreditHoursPerCourse: Number(e.target.value) })}
                inputProps={{ min: 0, step: 0.5 }}
                fullWidth
              />
              <TextField
                label="Minimum Annual CPE Hours"
                type="number"
                value={settings.cpe.minimumAnnualHours}
                onChange={(e) => setCpe({ minimumAnnualHours: Number(e.target.value) })}
                inputProps={{ min: 0 }}
                fullWidth
                helperText="Total hours required per user per compliance year."
              />
              <TextField
                label="Compliance Year Start (MM-DD)"
                value={settings.cpe.complianceYearStart}
                onChange={(e) => setCpe({ complianceYearStart: e.target.value })}
                fullWidth
                placeholder="01-01"
                helperText="Month-day when the CPE year resets (e.g., 01-01 for Jan 1)."
              />
              <TextField
                label="Grace Period (days)"
                type="number"
                value={settings.cpe.gracePeriodDays}
                onChange={(e) => setCpe({ gracePeriodDays: Number(e.target.value) })}
                inputProps={{ min: 0 }}
                fullWidth
                helperText="Additional days after year-end before compliance violation is flagged."
              />
            </Box>
          </TabPanel>
          {/* Search */}
          <TabPanel value={tab} index={5}>
            <SectionHeader title="Meilisearch" description="Full-text search engine configuration." />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: 560 }}>
              <TextField
                label="Host URL"
                placeholder="http://localhost:7700"
                value={settings.search.meilisearchHost}
                onChange={(e) => setSearch({ meilisearchHost: e.target.value })}
                fullWidth
                helperText="The Meilisearch instance URL (include port)."
              />
              <TextField
                label="API Key"
                type={showMeilisearchKey ? 'text' : 'password'}
                value={settings.search.meilisearchApiKey}
                onChange={(e) => setSearch({ meilisearchApiKey: e.target.value })}
                fullWidth
                helperText="Master key or a search-scoped API key."
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowMeilisearchKey((v) => !v)}>
                        {showMeilisearchKey ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </TabPanel>
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
